import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Phone, Mail } from 'lucide-react';
import TamilNaduMap from '../components/WorldMap';
import Field from '../components/Field';
import { useToast } from '../context/ToastContext';
import { api } from '../utils/api';

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', message: '' });
  const [errors, setErrors] = useState<Record<string,string>>({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e: Record<string,string> = {};
    if (!form.firstName.trim()) e.firstName = 'First name is required.';
    if (!form.email.trim())     e.email     = 'Email is required.';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email.';
    if (!form.message.trim())   e.message   = 'Please write a message.';
    return e;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({}); setLoading(true);
    
    try {
      await api.post('/contact', {
        name: `${form.firstName} ${form.lastName}`.trim(),
        email: form.email,
        message: form.message
      });
      
      toast('Message sent! We\'ll get back to you within one business day.', 'success');
      setForm({ firstName: '', lastName: '', email: '', message: '' });
    } catch (error) {
      toast('Failed to send message. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const set = (k: keyof typeof form) => (v: string) => setForm(p => ({...p, [k]: v}));

  return (
    <div className="container-fluid bg-white pt-10">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-16">
          {/* Info */}
          <div>
            <h1 className="text-[clamp(1.8rem,4vw,3rem)] font-black mb-4 text-[var(--color-text)] tracking-tight">Get In Touch</h1>
            <p className="text-[var(--color-muted)] mb-10 text-base leading-relaxed">Ready to start your investment journey? Reach out and we'll get back to you within one business day.</p>
            <div className="space-y-6">
              {[
                { icon: Phone, label: 'Phone',  href: 'tel:+918248276830', val: '+91 93453 51177',                   val1: '+91 93453 65089' },
                { icon: Mail,  label: 'Email',  href: 'mailto:stonebenchinvestments@gmail.com', val: 'stonebenchinvestments@gmail.com',   sub: 'Reply within 1 business day' },
              ].map((c, i) => {
                const Icon = c.icon;
                return (
                  <a key={i} href={c.href} className="flex items-start gap-5 group">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 transition-all group-hover:scale-105" style={{ background: 'rgba(95,156,76,0.1)' }}>
                      <Icon className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--color-muted)] mb-0.5">{c.label}</p>
                      <p className="text-base sm:text-lg font-bold text-[var(--color-text)] group-hover:text-[var(--color-accent)] transition-colors break-all">{c.val}</p>
                      {c.val1 && <p className="text-base sm:text-lg font-bold text-[var(--color-text)] group-hover:text-[var(--color-accent)] transition-colors break-all">{c.val1}</p>}
                      <p className="text-sm text-[var(--color-muted)]">{c.sub}</p>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>

          {/* Form */}
          <div className="card p-6 sm:p-8 card-hover-glow">
            <h2 className="text-xl font-bold mb-6 text-[var(--color-text)]">Send a Message</h2>
            <form className="space-y-4" onSubmit={handleSubmit} noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="First Name" error={errors.firstName} required>
                  <input className={`input ${errors.firstName ? 'border-red-400' : ''}`} value={form.firstName}
                    onChange={e => set('firstName')(e.target.value)} placeholder="John"/>
                </Field>
                <Field label="Last Name">
                  <input className="input" value={form.lastName} onChange={e => set('lastName')(e.target.value)} placeholder="Doe"/>
                </Field>
              </div>
              <Field label="Email Address" error={errors.email} required>
                <input type="email" className={`input ${errors.email ? 'border-red-400' : ''}`} value={form.email}
                  onChange={e => set('email')(e.target.value)} placeholder="you@example.com"/>
              </Field>
              <Field label="Message" error={errors.message} required>
                <textarea className={`input ${errors.message ? 'border-red-400' : ''}`} rows={4} value={form.message}
                  onChange={e => set('message')(e.target.value)} placeholder="How can we help you?"/>
              </Field>
              <button type="submit" disabled={loading} className="btn btn-primary w-full py-3 rounded-xl text-sm disabled:opacity-60 pt-2">
                {loading ? 'Sending…' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
        <TamilNaduMap />
      </motion.div>
    </div>
  );
}
